// lib/spam-protection.ts

// Secret salt used for hashing captcha tokens
const CAPTCHA_SALT = process.env.CAPTCHA_SECRET || 'tampa_terrazzo_anti_spam_salt_2026'

/**
 * Generate a random math captcha question and an obfuscated validation token
 */
export function generateCaptchaChallenge(): { num1: number; num2: number; token: string } {
  const num1 = Math.floor(Math.random() * 9) + 1 // 1-9
  const num2 = Math.floor(Math.random() * 9) + 1 // 1-9
  const sum = num1 + num2
  const timestamp = Date.now()

  // Simple, deterministic checksum
  const signature = createSimpleSignature(`${sum}:${timestamp}:${CAPTCHA_SALT}`)
  
  // Format: base64(sum:timestamp:signature)
  const tokenPayload = `${sum}:${timestamp}:${signature}`
  const token = typeof btoa !== 'undefined' 
    ? btoa(tokenPayload) 
    : Buffer.from(tokenPayload).toString('base64')

  return { num1, num2, token }
}

/**
 * Verify if the submitted captcha matches the token and is within valid time window (15 mins)
 */
export function verifyCaptcha(userAnswer: number | undefined | null, token: string | undefined | null): boolean {
  if (typeof userAnswer !== 'number' || isNaN(userAnswer) || !token) {
    return false
  }

  try {
    const decoded = typeof atob !== 'undefined'
      ? atob(token)
      : Buffer.from(token, 'base64').toString('utf-8')

    const parts = decoded.split(':')
    if (parts.length !== 3) return false

    const [sumStr, timestampStr, signature] = parts
    const expectedSum = parseInt(sumStr, 10)
    const timestamp = parseInt(timestampStr, 10)

    if (isNaN(expectedSum) || isNaN(timestamp)) return false

    // Verify signature
    const expectedSignature = createSimpleSignature(`${expectedSum}:${timestamp}:${CAPTCHA_SALT}`)
    if (signature !== expectedSignature) {
      return false
    }

    // Check expiry (15 minutes = 900,000 ms)
    const now = Date.now()
    if (now - timestamp > 15 * 60 * 1000) {
      return false // Expired captcha
    }

    // Must not be from the future (> 60s clock drift allowed)
    if (timestamp - now > 60 * 1000) {
      return false
    }

    return userAnswer === expectedSum
  } catch {
    return false
  }
}

/**
 * Lightweight hash/checksum function that runs in both browser and Node runtime
 */
function createSimpleSignature(input: string): string {
  let hash = 5381
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) + input.charCodeAt(i)
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Honeypot check: If the hidden honeypot field is filled, it's definitely a bot
 */
export function isHoneypotTriggered(honeypotValue: unknown): boolean {
  if (typeof honeypotValue === 'string') {
    return honeypotValue.trim().length > 0
  }
  return Boolean(honeypotValue)
}

/**
 * Submission speed check: Bots submit forms in < 2.5 seconds
 */
export function isSubmittedTooFast(formStartTime: number | undefined, minSeconds = 2.5): boolean {
  if (!formStartTime || isNaN(formStartTime)) return false
  const elapsedSeconds = (Date.now() - formStartTime) / 1000
  return elapsedSeconds > 0 && elapsedSeconds < minSeconds
}

/**
 * Content spam heuristics: Checks for spam keywords, link count, HTML tags, and Cyrillic/spam scripts
 */
export interface SpamCheckResult {
  isSpam: boolean
  reason?: string
}

const SPAM_KEYWORDS = [
  'viagra', 'cialis', 'casino', 'poker', 'roulette', 'slot machine', 'gambling',
  'crypto', 'bitcoin', 'usdt', 'ethereum', 'trading bot', 'binance', 'wallet recovery',
  'forex trading', 'investment profit', 'passive income guarantee',
  'guest post', 'da90', 'da 90', 'seo backlink', 'dofollow link', 'rank #1 on google',
  'rank on google', 'first page of google', 'domain authority', 'increase dr',
  'dating site', 'escort service', 'sugar baby', 'adult dating',
  'telegram.me', 't.me/', 'wa.me/', 'bit.ly/', 'tinyurl.com/', 'goo.gl/'
]

// Common spam Top-Level Domains
const SUSPICIOUS_TLDS = [
  '.ru', '.cn', '.xyz', '.top', '.tk', '.ml', '.ga', '.cf', '.gq', '.buzz', '.click', '.monster'
]

export function analyzeSpamContent(data: {
  name: string
  email: string
  phone: string
  message?: string
}): SpamCheckResult {
  const combinedText = `${data.name} ${data.email} ${data.message || ''}`.toLowerCase()
  const name = data.name.trim().toLowerCase()
  const phone = data.phone.trim().toLowerCase()
  const message = (data.message || '').trim().toLowerCase()

  // 1. URLs inside name or phone field (common bot pattern)
  const urlRegex = /(https?:\/\/|www\.|ftp:\/\/|[a-z0-9.-]+\.[a-z]{2,6}\/)/i
  if (urlRegex.test(name) || urlRegex.test(phone)) {
    return { isSpam: true, reason: 'Name or phone cannot contain URLs' }
  }

  // 2. HTML or BBCode tags inside message or name
  const htmlTagRegex = /<[^>]+>|\[url=[^\]]+\]|\[link=[^\]]+\]/i
  if (htmlTagRegex.test(combinedText)) {
    return { isSpam: true, reason: 'HTML tags and BBCode are not allowed' }
  }

  // 3. Count URLs in message (more than 1 URL in a contact message is usually spam)
  const urlMatches = message.match(/https?:\/\/|[a-z0-9.-]+\.[a-z]{2,6}\b/gi) || []
  if (urlMatches.length > 1) {
    return { isSpam: true, reason: 'Too many links included in message' }
  }

  // 4. Cyrillic characters (Russian spam bot attacks on local Florida business)
  const cyrillicRegex = /[\u0400-\u04FF]/
  if (cyrillicRegex.test(data.name) || cyrillicRegex.test(data.message || '')) {
    return { isSpam: true, reason: 'Non-English script detected' }
  }

  // 5. Check suspicious TLDs
  for (const tld of SUSPICIOUS_TLDS) {
    if (combinedText.includes(tld)) {
      return { isSpam: true, reason: `Suspicious link (${tld}) detected` }
    }
  }

  // 6. Check spam keywords
  for (const keyword of SPAM_KEYWORDS) {
    if (combinedText.includes(keyword)) {
      return { isSpam: true, reason: `Spam phrase detected: "${keyword}"` }
    }
  }

  return { isSpam: false }
}

/**
 * In-memory sliding window rate limiter
 */
interface RateLimitEntry {
  timestamps: number[]
}

const rateLimitStore = new Map<string, RateLimitEntry>()

// Clean up old entries every 10 minutes
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    const now = Date.now()
    const expiry = 10 * 60 * 1000 // 10 minutes
    for (const [ip, entry] of rateLimitStore.entries()) {
      entry.timestamps = entry.timestamps.filter(t => now - t < expiry)
      if (entry.timestamps.length === 0) {
        rateLimitStore.delete(ip)
      }
    }
  }, 10 * 60 * 1000)
}

export function isRateLimited(
  ip: string, 
  maxRequests = 5, 
  windowMs = 10 * 60 * 1000
): boolean {
  if (!ip || ip === '127.0.0.1' || ip === '::1') {
    // In local dev, allow more headroom
    maxRequests = 30
  }

  const now = Date.now()
  const entry = rateLimitStore.get(ip) || { timestamps: [] }
  
  // Filter timestamps within current window
  entry.timestamps = entry.timestamps.filter(t => now - t < windowMs)

  if (entry.timestamps.length >= maxRequests) {
    return true
  }

  entry.timestamps.push(now)
  rateLimitStore.set(ip, entry)
  return false
}

/**
 * Extract client IP address from Next.js request headers
 */
export function getClientIp(request: Request): string {
  const forwardedFor = request.headers.get('x-forwarded-for')
  if (forwardedFor) {
    return forwardedFor.split(',')[0].trim()
  }
  const realIp = request.headers.get('x-real-ip')
  if (realIp) {
    return realIp.trim()
  }
  return '127.0.0.1'
}
